import type {ComponentProps, CSSProperties, ReactNode} from 'react';
import {Badge} from '@/components/ui/badge';
import {Skeleton} from '@/components/ui/skeleton';
import {Card, CardTitle, CardDescription} from '@/components/ui/card';
import {Avatar, AvatarImage, AvatarFallback} from '@/components/ui/avatar';
import {cn} from '@/lib/utils';
import {Icon, type IconProp} from '@/app/components/Icon';

export type Tone = 'success' | 'info' | 'warning' | 'warn' | 'danger' | 'error' | 'secondary' | 'contrast';
const tones: Record<Tone, string> = {
  success: 'bg-green-100 text-green-800',
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-900',
  warn: 'bg-amber-100 text-amber-900',
  danger: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
  secondary: 'bg-muted text-muted-foreground',
  contrast: 'bg-foreground text-background',
};
export interface StatusBadgeProps extends ComponentProps<typeof Badge> {
  tone?: Tone | null;
  icon?: IconProp;
}
export function StatusBadge({tone, icon, className, children, ...props}: StatusBadgeProps) {
  return (
    <Badge className={cn(tone && tones[tone], 'max-w-full', className)} {...props}>
      {icon && <Icon icon={icon} />}
      <span className="truncate">{children}</span>
    </Badge>
  );
}
export interface NoticeProps extends Omit<ComponentProps<'div'>, 'content'> {
  tone?: Tone | null;
  content?: ReactNode;
  text?: ReactNode;
}
const toneIcons: Partial<Record<Tone, IconProp>> = {
  success: 'check',
  info: 'info-circle',
  warning: 'exclamation-triangle',
  warn: 'exclamation-triangle',
  danger: 'times-circle',
  error: 'times-circle',
};
/** Plain `text` gets a centered layout with the tone's icon, like PrimeReact's Message; `content` is rendered as is. */
export function Notice({tone = 'info', content, text, children, className, ...props}: NoticeProps) {
  const icon = content == null && text != null && tone ? toneIcons[tone] : undefined;
  return (
    <div
      role={tone === 'error' || tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-center gap-2 rounded-md border p-3 text-sm',
        icon && 'justify-center',
        tone && tones[tone],
        className,
      )}
      {...props}
    >
      {icon && <Icon icon={icon} className="shrink-0" />}
      {content ?? text ?? children}
    </div>
  );
}
export function LoadingSkeleton({
  height,
  width,
  size,
  borderRadius,
  shape,
  style,
  ...props
}: ComponentProps<typeof Skeleton> & {
  height?: string;
  width?: string;
  size?: string;
  borderRadius?: string;
  shape?: 'circle' | 'rectangle';
}) {
  return (
    <Skeleton
      {...props}
      style={{
        height: height ?? size,
        width: width ?? size,
        borderRadius: shape === 'circle' ? '50%' : borderRadius,
        ...style,
      }}
    />
  );
}
export interface PanelProps {
  title?: ReactNode;
  subTitle?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Classes for the padded area that holds the title, content and footer. */
  bodyClassName?: string;
  style?: CSSProperties;
}
/**
 * Card laid out like PrimeReact's: the header sits flush above a 1rem-padded body, where the content and
 * footer are separated by 1rem. Headers and footers can bleed to the card's edges with `-m-4`.
 */
export function Panel({title, subTitle, header, footer, children, className, bodyClassName, style}: PanelProps) {
  return (
    <Card className={cn('gap-0 py-0', className)} style={style}>
      {header}
      <div data-slot="panel-body" className={cn('p-4', bodyClassName)}>
        {title && <CardTitle className="mb-2 text-lg leading-tight font-bold">{title}</CardTitle>}
        {subTitle && <CardDescription className="mb-2">{subTitle}</CardDescription>}
        <div data-slot="panel-content" className="py-4">
          {children}
        </div>
        {footer && (
          <div data-slot="panel-footer" className="pt-4">
            {footer}
          </div>
        )}
      </div>
    </Card>
  );
}
export function UserAvatar({
  image,
  label,
  icon,
  size,
  className,
  shape,
  onClick,
  ...props
}: Omit<ComponentProps<typeof Avatar>, 'onClick' | 'size'> & {
  image?: string;
  label?: string;
  icon?: IconProp;
  size?: 'normal' | 'large' | 'xlarge';
  shape?: string;
  onClick?: ComponentProps<'button'>['onClick'];
}) {
  const content = (
    <Avatar
      {...props}
      className={cn(
        'bg-zinc-200 text-zinc-600',
        size === 'large' && 'size-12 text-lg',
        size === 'xlarge' && 'size-24 text-3xl',
        className,
      )}
    >
      {image && <AvatarImage src={image} alt={label ?? 'Profile'} />}
      <AvatarFallback className="bg-transparent text-[length:inherit] text-inherit">
        {icon ? <Icon icon={icon} /> : label}
      </AvatarFallback>
    </Avatar>
  );
  return onClick ? (
    <button
      type="button"
      aria-label="Open account menu"
      className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onClick}
    >
      {content}
    </button>
  ) : (
    content
  );
}
export function AvatarStack({className, ...props}: ComponentProps<'div'>) {
  return <div className={cn('flex -space-x-2 [&>span]:ring-2 [&>span]:ring-background', className)} {...props} />;
}
export function Token({children, className}: {children?: ReactNode; className?: string}) {
  return (
    <Badge variant="secondary" className={className}>
      {children}
    </Badge>
  );
}
